import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useRouter,
} from '@tanstack/react-router';
import { AppShell } from './layout/AppShell';
import { meQuery } from './lib/session';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AuditPage } from './pages/admin/AuditPage';
import { BackupsPage } from './pages/admin/BackupsPage';
import { BrandingPage } from './pages/admin/BrandingPage';
import { DelegationsPage } from './pages/admin/DelegationsPage';
import { FileScanPage } from './pages/admin/FileScanPage';
import { IntegrationsPage } from './pages/admin/IntegrationsPage';
import { LegalEntitiesPage } from './pages/admin/LegalEntitiesPage';
import { LicensePage } from './pages/admin/LicensePage';
import { MemberCardPage } from './pages/admin/MemberCardPage';
import { MembersPage } from './pages/admin/MembersPage';
import { OrgPage } from './pages/admin/OrgPage';
import { PrivacyPage } from './pages/admin/PrivacyPage';
import { RolesPage } from './pages/admin/RolesPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { SystemStatusPage } from './pages/admin/SystemStatusPage';
import { PilotPage } from './pages/ai/PilotPage';
import { ManagerPage } from './pages/analytics/ManagerPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { InvitePage } from './pages/auth/InvitePage';
import { LoginPage } from './pages/auth/LoginPage';
import { RequestAccessPage } from './pages/auth/RequestAccessPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { SetupPage } from './pages/auth/SetupPage';
import { BudgetIndexPage } from './pages/budget/BudgetIndexPage';
import { CalendarPage } from './pages/CalendarPage';
import { CostArticlesPage } from './pages/CostArticlesPage';
import { ChangePage, ChangesPage } from './pages/changes/ChangesPage';
import { ContractPage, ContractsPage } from './pages/contracts/ContractsPage';
import { DevBoardPage } from './pages/devboard/DevBoardPage';
import { validateBoardSearch } from './pages/devboard/shared';
import { DocumentsPage } from './pages/documents/DocumentsPage';
import { ExecDocsPage } from './pages/documents/ExecDocsPage';
import { LettersPage } from './pages/documents/LettersPage';
import { WorkJournalPage } from './pages/documents/WorkJournalPage';
import { EstimatePage } from './pages/estimates/EstimatePage';
import { EstimatesPage } from './pages/estimates/EstimatesPage';
import { ResourcesPage } from './pages/estimates/ResourcesPage';
import { FinancePage } from './pages/finance/FinancePage';
import { FleetPage } from './pages/fleet/FleetPage';
import { InboxPage } from './pages/InboxPage';
import { ArticlePage } from './pages/kb/ArticlePage';
import { KbPage } from './pages/kb/KbPage';
import { NewArticlePage } from './pages/kb/NewArticlePage';
import { NormReader } from './pages/kb/NormReader';
import { LinksMapPage } from './pages/LinksMapPage';
import { LandingPage } from './pages/landing/LandingPage';
import { SignupConfirmPage } from './pages/landing/SignupConfirmPage';
import { ModulePage } from './pages/ModulePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LibraryImportPage, LibraryReviewsPage } from './pages/norms/Library';
import { NormItemPage, NormReleasePage } from './pages/norms/NormItemPage';
import { NormsPage } from './pages/norms/NormsPage';
import { ProfilePage } from './pages/ProfilePage';
import { AnnouncementsPage as PlatformAnnouncementsPage } from './pages/platform/AnnouncementsPage';
import { EventsPage as PlatformEventsPage } from './pages/platform/EventsPage';
import { InvoicesPage as PlatformInvoicesPage } from './pages/platform/InvoicesPage';
import { MarketsPage as PlatformMarketsPage } from './pages/platform/MarketsPage';
import { PilotPage as PlatformPilotPage } from './pages/platform/PilotPage';
import { PlansPage as PlatformPlansPage } from './pages/platform/PlansPage';
import { PlatformLayout } from './pages/platform/PlatformLayout';
import { RequestsPage as PlatformRequestsPage } from './pages/platform/RequestsPage';
import { SalesPage as PlatformSalesPage } from './pages/platform/SalesPage';
import { SecurityPage as PlatformSecurityPage } from './pages/platform/SecurityPage';
import { TeamPage as PlatformTeamPage } from './pages/platform/TeamPage';
import { TenantPage as PlatformTenantPage } from './pages/platform/TenantPage';
import { TenantsPage as PlatformTenantsPage } from './pages/platform/TenantsPage';
import {
  UserCardPage as PlatformUserCardPage,
  UsersPage as PlatformUsersPage,
} from './pages/platform/UsersPage';
import { VersionsPage as PlatformVersionsPage } from './pages/platform/VersionsPage';
import { PortfolioPage } from './pages/portfolio/PortfolioPage';
import { ProcurementPage } from './pages/procurement/ProcurementPage';
import { RegistryPage } from './pages/procurement/payments';
import { RequestPage } from './pages/procurement/RequestPage';
import { RfqPage } from './pages/procurement/RfqPage';
import { ProjectPage } from './pages/projects/ProjectPage';
import { ProjectsListPage } from './pages/projects/ProjectsListPage';
import { FileViewerPage, SheetComparePage } from './pages/psd/FileViewerPage';
import { PsdPage, SetPage, TransmittalPage } from './pages/psd/PsdPage';
import { SharePage } from './pages/psd/SharePage';
import { SheetPage } from './pages/psd/SheetPage';
import { BriefingJournalPage, BriefingPage as QualityBriefingPage } from './pages/quality/BriefingPage';
import { InspectionPage as QualityInspectionPage } from './pages/quality/InspectionPage';
import {
  ObservationPage as QualityObservationPage,
  ObservePage as QualityObservePage,
} from './pages/quality/ObservationPages';
import { PermitPage as QualityPermitPage } from './pages/quality/PermitPage';
import { QualityPage } from './pages/quality/QualityPage';
import { TemplatePage as QualityTemplatePage } from './pages/quality/TemplatePage';
import { RoadmapPage } from './pages/RoadmapPage';
import { BuilderPage } from './pages/reports/BuilderPage';
import { CustomReportPage } from './pages/reports/CustomReportPage';
import { ListsIndexPage, SourceListPage } from './pages/reports/ListsPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { RunPage } from './pages/reports/RunPage';
import { ResourcesSectionPage } from './pages/resources/ResourcesPage';
import { ContractorSchedulePage } from './pages/schedule/ContractorSchedulePage';
import { SchedulePage, SchedulesPage } from './pages/schedule/SchedulePage';
import { DaySummaryPage } from './pages/site/DaySummaryPage';
import { ExtraWorkActPage, ExtraWorkNewPage, ExtraWorksListPage } from './pages/site/ExtraWorksPage';
import { MissingReportsPage } from './pages/site/MissingReportsPage';
import { MyDayPage } from './pages/site/MyDayPage';
import { SiteReviewPage } from './pages/site/review/ReviewPage';
import { SitePage } from './pages/site/SitePage';
import { SitePhotosPage } from './pages/site/SitePhotosPage';
import { StartPage } from './pages/start/StartPage';
import { InspectionPage } from './pages/supervision/InspectionPage';
import { QuestionPage, RemarkPage, SupervisionPage } from './pages/supervision/SupervisionPage';
import { VisitPage } from './pages/supervision/VisitPage';
import { SupplierCabinetPage, SupplierRfqReplyPage } from './pages/suppliers/SupplierCabinetPage';
import { SupplierPage, SuppliersPage } from './pages/suppliers/SuppliersPage';
import { SuggestionPage } from './pages/support/SuggestionPage';
import { SupportPage } from './pages/support/SupportPage';
import { TicketPage } from './pages/support/TicketPage';
import { VideoPage } from './pages/video/VideoPage';
import { WhatsNewPage } from './pages/WhatsNewPage';
import { DocumentPage as WarehouseDocumentPage } from './pages/warehouse/DocumentPage';
import { InventoryPage as WarehouseInventoryPage } from './pages/warehouse/InventoryPage';
import { ItemPage as WarehouseItemPage } from './pages/warehouse/ItemPage';
import { LabelsPage as WarehouseLabelsPage } from './pages/warehouse/LabelsPage';
import { ToolPage as WarehouseToolPage } from './pages/warehouse/ToolPage';
import { WarehouseCardPage } from './pages/warehouse/WarehouseCardPage';
import { WarehousePage } from './pages/warehouse/WarehousePage';
import { Button } from './ui/button';
import { Toaster } from './ui/feedback';

interface RouterContext {
  queryClient: QueryClient;
}

function RootError({ error }: { error: unknown }) {
  const router = useRouter();
  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center gap-3 px-6">
      <h1 className="text-[22px] font-semibold text-ink">Портал не отвечает</h1>
      <p className="text-[14px] text-ink-2">
        {error instanceof Error ? error.message : 'Не удалось загрузить данные.'} Проверьте связь и повторите.
      </p>
      <div>
        <Button variant="primary" onClick={() => router.invalidate()}>
          Повторить
        </Button>
      </div>
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
  notFoundComponent: NotFoundPage,
  errorComponent: RootError,
});

/**
 * Адрес внутри портала, куда вернуться после входа: только свой путь — начинается с одной «/», без «\\» и
 * управляющих символов (чтобы не стать внешней ссылкой при любом способе перехода) и не сама страница входа.
 */
export function safeNext(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  if (!/^\/(?![/\\])/.test(v) || v.includes('\\') || v.startsWith('/login')) return undefined;
  for (const ch of v) {
    const code = ch.charCodeAt(0);
    if (code < 32 || code === 127) return undefined;
  }
  return v;
}
/** Почта для подстановки в поле формы входа и восстановления пароля (ДС-19): только похожая на адрес строка */
function safeEmail(v: unknown): string | undefined {
  return typeof v === 'string' && v.length <= 254 && /^[^\s@]+@[^\s@]+$/.test(v) ? v : undefined;
}
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (s: Record<string, unknown>): { next?: string; email?: string } => ({
    next: safeNext(s.next),
    email: safeEmail(s.email),
  }),
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me) throw redirect({ to: search.next ?? '/' });
  },
});

const setupRoute = createRoute({ getParentRoute: () => rootRoute, path: '/setup', component: SetupPage });
/* Главная страница без входа (ГЛ-01): открыта всем; вошедшему показывает кнопку «В портал» */
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: LandingPage,
});
/* Заявка на доступ без входа (ГЛ-04); вошедшему она не нужна — на главную. Демо-пользователь — исключение:
   посмотрел демо и просит доступ для своей компании */
const requestAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/request-access',
  component: RequestAccessPage,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me && !me.user.demoRole) throw redirect({ to: '/' });
  },
});
/* Подтверждение почты при самостоятельной регистрации компании (ГЛ-05) */
const signupConfirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup/confirm/$token',
  component: SignupConfirmPage,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InvitePage,
});
/* Восстановление пароля по почте (ДС-19): запрос ссылки и новый пароль по ссылке из письма — без входа */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
  validateSearch: (s: Record<string, unknown>): { email?: string } => ({ email: safeEmail(s.email) }),
});
/* Ссылка из письма — после «#» (`/reset-password#<ссылка>`): до сервера и в журналы запросов она не доходит */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

/* Поток 4: комплект ПСД по ссылке для получателя без учётной записи (ПД-24) */
const psdShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/psd/$token',
  component: SharePage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppShell,
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    // Без входа адрес портала открывает главную страницу (ГЛ-01), остальные адреса — вход,
    // а после входа — обратно на запрошенную страницу (ссылки из писем, сводок и Telegram)
    if (!me) {
      if (location.pathname === '/') throw redirect({ to: '/welcome' });
      throw redirect({ to: '/login', search: { next: location.href } });
    }
  },
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: PortfolioPage });
/* «Обзор» без выбранного проекта — список карточек проектов (6.1) */
const projectsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects',
  component: ProjectsListPage,
});
const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects/$projectId',
  component: ProjectPage,
});
const projectTabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects/$projectId/$tab',
  component: ProjectPage,
  validateSearch: (s: Record<string, unknown>) => ({ line: typeof s.line === 'string' ? s.line : undefined }),
});
const suppliersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/suppliers',
  component: SuppliersPage,
});
const supplierCabinetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/suppliers/cabinet',
  component: SupplierCabinetPage,
});
const supplierCabinetRfqRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/suppliers/cabinet/rfqs/$rfqId',
  component: SupplierRfqReplyPage,
});
const supplierRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/suppliers/$supplierId',
  component: SupplierPage,
});
const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inbox',
  component: InboxPage,
});
/* Личный календарь и подписка ICS (ЗД-11, ЗД-12) */
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/calendar',
  component: CalendarPage,
});
const procurementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement',
  component: ProcurementPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: s.tab === 'registries' || s.tab === 'payments' || s.tab === 'rfqs' ? (s.tab as string) : undefined,
  }),
});
const requestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/$requestId',
  component: RequestPage,
});
const rfqRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/rfqs/$rfqId',
  component: RfqPage,
});
const registryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/procurement/registries/$registryId',
  component: RegistryPage,
});
const contractsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/contracts',
  component: ContractsPage,
});
const contractRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/contracts/$contractId',
  component: ContractPage,
});
const financeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/finance',
  component: FinancePage,
  validateSearch: (s: Record<string, unknown>) => {
    const day = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
    return {
      tab: ['docs', 'limits', 'export', 'cashflow', 'advances', 'registry'].includes(s.tab as string)
        ? (s.tab as string)
        : undefined,
      // Фильтры календаря в адресе: календарь проекта открывается ссылкой, «назад» возвращает фильтр
      projectId:
        typeof s.projectId === 'string' && /^[0-9a-f-]{36}$/i.test(s.projectId) ? s.projectId : undefined,
      from: day(s.from),
      to: day(s.to),
      by: s.by === 'days' ? ('days' as const) : undefined,
      // Реестр платежей (чат fin, ФН-03): карточка реестра открывается ссылкой из «Ждёт меня»
      registryId:
        typeof s.registryId === 'string' && /^[0-9a-f-]{36}$/i.test(s.registryId) ? s.registryId : undefined,
    };
  },
});
/** Демо-компании ход разработки и карта связей не показываются: ссылки ведут на главную */
async function demoRedirect({
  context,
}: {
  context: { queryClient: { ensureQueryData: (q: typeof meQuery) => Promise<unknown> } };
}) {
  const me = (await context.queryClient.ensureQueryData(meQuery)) as {
    tenant?: { isDemo?: boolean } | null;
  } | null;
  if (me?.tenant?.isDemo) throw redirect({ to: '/' });
}
const roadmapRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/roadmap',
  component: RoadmapPage,
  beforeLoad: demoRedirect,
});
const linksMapRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/roadmap/map',
  component: LinksMapPage,
  beforeLoad: demoRedirect,
});
// Доска задач разработки (чат board, 03.11.2026): задачи чатов Claude и команды портала, кто что взял
const devboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/devboard',
  component: DevBoardPage,
  validateSearch: validateBoardSearch,
});
const whatsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/whats-new',
  component: WhatsNewPage,
});
/** «С чего начать» (БЗ-31): первые шаги, порядок дня и разделы по правам человека */
const startRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/start',
  component: StartPage,
});
const costArticlesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cost-articles',
  component: CostArticlesPage,
});
const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/profile',
  component: ProfilePage,
});
// Поток 5 — склад (раздел 6.13)
const warehouseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/warehouse',
  component: WarehousePage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: typeof s.tab === 'string' ? s.tab : undefined,
    poa: typeof s.poa === 'string' ? s.poa : undefined,
    // Подбор со склада из заявки на закуп (СК-12): что ищем и строка заявки
    find: typeof s.find === 'string' ? s.find : undefined,
    unit: typeof s.unit === 'string' ? s.unit : undefined,
    qty: typeof s.qty === 'string' ? s.qty : undefined,
    project: typeof s.project === 'string' ? s.project : undefined,
    line: typeof s.line === 'string' ? s.line : undefined,
  }),
});
const warehouseDocRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/warehouse/documents/$docId',
  component: WarehouseDocumentPage,
});
const warehouseItemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/warehouse/items/$itemId',
  component: WarehouseItemPage,
});
const warehouseCardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/warehouse/warehouses/$warehouseId',
  component: WarehouseCardPage,
});
const warehouseInventoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/warehouse/inventories/$inventoryId',
  component: WarehouseInventoryPage,
});
const warehouseToolRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/warehouse/tools/$unitId',
  component: WarehouseToolPage,
});
const warehouseLabelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/warehouse/labels',
  component: WarehouseLabelsPage,
  validateSearch: (s: Record<string, unknown>) => ({
    items: typeof s.items === 'string' ? s.items : undefined,
    units: typeof s.units === 'string' ? s.units : undefined,
    doc: typeof s.doc === 'string' ? s.doc : undefined,
    format: typeof s.format === 'string' ? s.format : undefined,
  }),
});

const adminRoute = createRoute({ getParentRoute: () => appRoute, path: '/admin', component: AdminLayout });
const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/members' });
  },
});
const adminMembersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'members',
  component: MembersPage,
});
const adminMemberCardRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'members/$memberId',
  component: MemberCardPage,
});
const adminDelegationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'delegations',
  component: DelegationsPage,
});
const adminRolesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'roles',
  component: RolesPage,
});
const adminLeRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'legal-entities',
  component: LegalEntitiesPage,
});
const adminOrgRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'org',
  component: OrgPage,
});
/* Журнал изменений (ДС-11): вкладка, запись «схема.таблица:номер», отправка — по ссылке из карточки и из письма */
const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'audit',
  component: AuditPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: typeof s.tab === 'string' ? s.tab : undefined,
    record: typeof s.record === 'string' ? s.record : undefined,
    act: typeof s.act === 'string' ? s.act : undefined,
  }),
});
const adminSettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'settings',
  component: SettingsPage,
});
/* Резервные копии компании (АД-18) */
const adminBackupsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'backups',
  component: BackupsPage,
});
const adminLicenseRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'license',
  component: LicensePage,
});
/* Оформление и региональные форматы (ЛЦ-08, МР-05), запросы субъектов данных (МР-10) */
const adminBrandingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'branding',
  component: BrandingPage,
});
const adminPrivacyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'privacy',
  component: PrivacyPage,
});
/* Антивирус и карантин файлов (DEV-10) */
const adminFileScanRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'files',
  component: FileScanPage,
});

/* Обмен с внешними системами (раздел 7 ТЗ, ИН-01–05) */
const adminIntegrationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'integrations',
  component: IntegrationsPage,
});

/* Состояние системы: каналы, доставка, копии и мониторинг (раздел 6.24) */
const adminStatusRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'status',
  component: SystemStatusPage,
});

/* Кабинет владельца портала (ТЗ 6.29–6.30): компании, тарифы, заявки, журнал */
const platformRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform',
  component: PlatformLayout,
});
const platformIndexRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/',
  component: PlatformTenantsPage,
});
const platformTenantRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'tenants/$id',
  component: PlatformTenantPage,
});
const platformPlansRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'plans',
  component: PlatformPlansPage,
});
const platformRequestsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'requests',
  component: PlatformRequestsPage,
});
const platformInvoicesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'invoices',
  component: PlatformInvoicesPage,
});
const platformMarketsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'markets',
  component: PlatformMarketsPage,
});
const platformSalesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'sales',
  component: PlatformSalesPage,
});
const platformEventsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'events',
  component: PlatformEventsPage,
});
// Контрольная проверка модели Pilot (ИИ-13, чат Д)
const platformPilotRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'pilot',
  component: PlatformPilotPage,
});
// Супер-админка (ЛЦ-09…ЛЦ-13): люди всех компаний, безопасность входа, объявления, команда портала
const platformUsersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'users',
  component: PlatformUsersPage,
});
const platformUserRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'users/$id',
  component: PlatformUserCardPage,
});
const platformSecurityRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'security',
  component: PlatformSecurityPage,
});
const platformAnnouncementsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'announcements',
  component: PlatformAnnouncementsPage,
});
const platformTeamRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'team',
  component: PlatformTeamPage,
});
// Версии портала: возврат назад и вперёд, копия базы, проверка всей базы (НФ-21, НФ-12)
const platformVersionsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: 'versions',
  component: PlatformVersionsPage,
});

/* Поток 4: ПСД, надзор и вопросы к проекту */
const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
/* Поток 1: помощь и поддержка */
const supportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/support',
  component: SupportPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab:
      s.tab === 'suggestions' ||
      s.tab === 'queue' ||
      s.tab === 'report' ||
      // Премии за предложения и активность и анализ предложений (БЗ-32…БЗ-35)
      s.tab === 'rewards' ||
      s.tab === 'analysis'
        ? (s.tab as string)
        : undefined,
    status: str(s.status),
    /** Период премирования: день ГГГГ-ММ-ДД внутри месяца или квартала; несуществующая дата — текущий период */
    period:
      typeof s.period === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(s.period) &&
      !Number.isNaN(Date.parse(`${s.period}T00:00:00Z`)) &&
      new Date(`${s.period}T00:00:00Z`).toISOString().slice(0, 10) === s.period
        ? s.period
        : undefined,
  }),
});
const supportTicketRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/support/tickets/$id',
  component: TicketPage,
});
const supportSuggestionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/support/suggestions/$id',
  component: SuggestionPage,
});
const psdRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/psd',
  component: PsdPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: str(s.project),
    tab: s.tab === 'transmittals' || s.tab === 'rule' ? (s.tab as string) : undefined,
    q: str(s.q),
    // Папки ПСД: объект (пятно) и раздел (марка); view=list — прежний список альбомов
    object: str(s.object),
    mark: str(s.mark),
    view: s.view === 'list' ? 'list' : undefined,
  }),
});
const psdSetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/psd/sets/$setId',
  component: SetPage,
});
const psdSheetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/psd/sheets/$sheetId',
  component: SheetPage,
});
const psdFileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/psd/files/$fileId',
  component: FileViewerPage,
});
const psdCompareRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/psd/sheets/$sheetId/compare',
  component: SheetComparePage,
  validateSearch: (s: Record<string, unknown>) => ({ a: str(s.a), b: str(s.b) }),
});
const psdTransmittalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/psd/transmittals/$transmittalId',
  component: TransmittalPage,
});
const supervisionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/supervision',
  component: SupervisionPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: str(s.project),
    tab:
      s.tab === 'questions' ||
      s.tab === 'stats' ||
      s.tab === 'visits' ||
      s.tab === 'inspections' ||
      s.tab === 'report'
        ? (s.tab as string)
        : undefined,
    set: str(s.set),
    status: str(s.status),
    source: str(s.source),
    q: str(s.q),
    object: str(s.object),
    mark: str(s.mark),
    contractor: str(s.contractor),
    assignee: str(s.assignee),
    due: str(s.due),
  }),
});
const remarkRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/supervision/remarks/$remarkId',
  component: RemarkPage,
  validateSearch: (s: Record<string, unknown>) => ({ visit: str(s.visit) }),
});
const inspectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/supervision/inspections/$inspectionId',
  component: InspectionPage,
});
const visitRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/supervision/visits/$visitId',
  component: VisitPage,
});
const changesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/changes',
  component: ChangesPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: str(s.project),
    status: str(s.status),
    tab: s.tab === 'trend' ? 'trend' : undefined,
  }),
});
const changeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/changes/$changeId',
  component: ChangePage,
});
const questionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/supervision/questions/$questionId',
  component: QuestionPage,
});

/* Поток 2: сметы и нормативная база */
const estimatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/estimates',
  component: EstimatesPage,
  validateSearch: (s: Record<string, unknown>) => ({
    projectId: typeof s.projectId === 'string' ? s.projectId : undefined,
  }),
});
const estimateResourcesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/estimates/resources',
  component: ResourcesPage,
  validateSearch: (s: Record<string, unknown>) => ({
    projectId: typeof s.projectId === 'string' ? s.projectId : undefined,
  }),
});
const estimateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/estimates/$estimateId',
  component: EstimatePage,
});
const normsRoute = createRoute({ getParentRoute: () => appRoute, path: '/norms', component: NormsPage });
const normItemRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/norms/items/$itemId',
  component: NormItemPage,
});
const normReleaseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/norms/releases/$releaseId',
  component: NormReleasePage,
});
const normLibraryImportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/norms/library/imports/$importId',
  component: LibraryImportPage,
});
const normLibraryReviewsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/norms/library/reviews',
  component: LibraryReviewsPage,
});

const schedulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/schedule',
  component: SchedulesPage,
});
/** Экран руководителя (6.18): закупки, деньги, сроки и маржа */
const analyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/analytics',
  component: ManagerPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    tab: s.tab === 'ev' || s.tab === 'bridge' || s.tab === 'sections' ? (s.tab as string) : undefined,
  }),
});
/** Отчёты и настраиваемые списки (6.18): готовые отчёты, конструктор, списки с видами; статические пути раньше динамических */
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  component: ReportsPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: s.tab === 'mine' || s.tab === 'digest' || s.tab === 'ready' ? (s.tab as string) : undefined,
  }),
});
const reportsListsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/lists',
  component: ListsIndexPage,
});
const reportsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/lists/$source',
  component: SourceListPage,
});
const reportsBuilderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/builder',
  component: BuilderPage,
  validateSearch: (s: Record<string, unknown>) => ({
    id: typeof s.id === 'string' && /^[\da-f-]{36}$/i.test(s.id) ? s.id : undefined,
  }),
});
const reportsCustomRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/custom/$id',
  component: CustomReportPage,
});
const reportsRunRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/runs/$id',
  component: RunPage,
});
/** База знаний (6.22): статьи, нормы, подписки; статические пути раньше динамического */
const kbRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/kb',
  component: KbPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab:
      s.tab === 'queue' || s.tab === 'subscriptions' || s.tab === 'scans' || s.tab === 'articles'
        ? (s.tab as string)
        : undefined,
    q: typeof s.q === 'string' && s.q ? s.q.slice(0, 200) : undefined,
    section: typeof s.section === 'string' && s.section ? s.section.slice(0, 40) : undefined,
    mine: s.mine === '1' ? '1' : undefined,
  }),
});
const kbNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/kb/new',
  component: NewArticlePage,
});
const kbNormRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/kb/norms/$releaseId',
  component: NormReader,
  validateSearch: (s: Record<string, unknown>) => ({
    p: Number.isInteger(Number(s.p)) && Number(s.p) > 0 ? Number(s.p) : undefined,
    n: typeof s.n === 'string' && s.n ? s.n.slice(0, 40) : undefined,
    q: typeof s.q === 'string' && s.q ? s.q.slice(0, 200) : undefined,
    lang: s.lang === 'kk' ? ('kk' as const) : s.lang === 'ru' ? ('ru' as const) : undefined,
  }),
});
const kbArticleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/kb/$articleId',
  component: ArticlePage,
});
const resourcesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/resources',
  component: ResourcesSectionPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: typeof s.tab === 'string' ? s.tab : undefined,
    project: typeof s.project === 'string' ? s.project : undefined,
  }),
});
const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents',
  component: DocumentsPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' ? s.project : undefined,
  }),
});
const execDocsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents/exec',
  component: ExecDocsPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    object: typeof s.object === 'string' && /^[\da-f-]{36}$/i.test(s.object) ? s.object : undefined,
  }),
});
const workJournalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents/journals',
  component: WorkJournalPage,
  validateSearch: (s: Record<string, unknown>) => {
    const id = (v: unknown) => (typeof v === 'string' && /^[\da-f-]{36}$/i.test(v) ? v : undefined);
    const day = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
    return { project: id(s.project), object: id(s.object), from: day(s.from), to: day(s.to) };
  },
});
const lettersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents/letters',
  component: LettersPage,
  validateSearch: (s: Record<string, unknown>) => ({
    letter: typeof s.letter === 'string' && /^[\da-f-]{36}$/i.test(s.letter) ? s.letter : undefined,
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    direction: s.direction === 'in' || s.direction === 'out' ? s.direction : undefined,
    status: ['open', 'registered', 'in_work', 'answered', 'closed'].includes(s.status as string)
      ? (s.status as string)
      : undefined,
    mine: s.mine === true || s.mine === 'true' ? true : undefined,
    overdue: s.overdue === true || s.overdue === 'true' ? true : undefined,
    q: typeof s.q === 'string' && s.q ? s.q.slice(0, 100) : undefined,
  }),
});
const scheduleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/schedule/$projectId',
  component: SchedulePage,
});
/** Кабинет подрядчика «Мой график» (ПЛ-32) */
const contractorScheduleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/schedule/contractor',
  component: ContractorSchedulePage,
});

/** Разделы, которые появятся на следующих этапах: страница с их требованиями */
const siteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site',
  component: SitePage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    day: typeof s.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.day) ? s.day : undefined,
    draft: typeof s.draft === 'string' && /^[\da-f-]{36}$/i.test(s.draft) ? s.draft : undefined,
    report: typeof s.report === 'string' && /^[\da-f-]{36}$/i.test(s.report) ? s.report : undefined,
    package: typeof s.package === 'string' && /^[\da-f-]{36}$/i.test(s.package) ? s.package : undefined,
  }),
});
// Поток 7 — качество и ОТ (раздел 6.16)
const qhseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/qhse',
  component: QualityPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: ['inspections', 'templates', 'permits', 'briefings', 'credentials', 'observations'].includes(
      s.tab as string,
    )
      ? (s.tab as string)
      : undefined,
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    status: typeof s.status === 'string' ? s.status : undefined,
    source: typeof s.source === 'string' ? s.source : undefined,
  }),
});
const qhseInspectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/qhse/inspections/$inspectionId',
  component: QualityInspectionPage,
});
const qhseTemplateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/qhse/templates/$templateId',
  component: QualityTemplatePage,
});
const qhsePermitRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/qhse/permits/$permitId',
  component: QualityPermitPage,
});
const qhseBriefingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/qhse/briefings/$briefingId',
  component: QualityBriefingPage,
});
const qhseJournalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/qhse/briefings/journal',
  component: BriefingJournalPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
  }),
});
const qhseObservationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/qhse/observations/$observationId',
  component: QualityObservationPage,
});
const qhseObserveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/qhse/observe',
  component: QualityObservePage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
  }),
});
// Поток 8 — видеонаблюдение (раздел 6.31)
const videoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/video',
  component: VideoPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab:
      s.tab === 'events' || s.tab === 'log' || s.tab === 'legal' || s.tab === 'cameras' ? s.tab : undefined,
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    camera: typeof s.camera === 'string' && /^[\da-f-]{36}$/i.test(s.camera) ? s.camera : undefined,
    event: typeof s.event === 'string' && /^[\da-f-]{36}$/i.test(s.event) ? s.event : undefined,
    decision:
      s.decision === 'pending' ||
      s.decision === 'confirmed' ||
      s.decision === 'false_positive' ||
      s.decision === 'ignored' ||
      s.decision === 'all'
        ? s.decision
        : undefined,
  }),
});
// Поток 9 — техника, топливо и выработка (разделы 6.32, 6.33)
const fleetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/fleet',
  component: FleetPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: ['waybills', 'timesheet', 'fuel', 'cost', 'output'].includes(String(s.tab)) ? s.tab : undefined,
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    machine: typeof s.machine === 'string' && /^[\da-f-]{36}$/i.test(s.machine) ? s.machine : undefined,
  }),
});
// Пункты меню «Бюджет и маржа» и «Pilot» — настоящие страницы, а не заглушка раздела
const budgetIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/budget',
  component: BudgetIndexPage,
});
const pilotRoute = createRoute({ getParentRoute: () => appRoute, path: '/ai', component: PilotPage });
const moduleRoute = createRoute({ getParentRoute: () => appRoute, path: '$module', component: ModulePage });
const siteDayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site/day',
  component: MyDayPage,
  validateSearch: (s: Record<string, unknown>) => ({
    day: typeof s.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.day) ? s.day : undefined,
    days: Number(s.days) === 7 ? 7 : undefined,
  }),
});
const siteMissingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site/missing',
  component: MissingReportsPage,
  validateSearch: (s: Record<string, unknown>) => ({
    day: typeof s.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.day) ? s.day : undefined,
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
  }),
});
const siteSummaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site/summary',
  component: DaySummaryPage,
  validateSearch: (s: Record<string, unknown>) => ({
    day: typeof s.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.day) ? s.day : undefined,
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    object: typeof s.object === 'string' && /^[\da-f-]{36}$/i.test(s.object) ? s.object : undefined,
  }),
});
const siteExtraWorksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site/extra-works',
  component: ExtraWorksListPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    day: typeof s.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.day) ? s.day : undefined,
  }),
});
const siteExtraWorkNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site/extra-works/new',
  component: ExtraWorkNewPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    day: typeof s.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.day) ? s.day : undefined,
  }),
});
const siteExtraWorkActRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site/extra-works/$actId',
  component: ExtraWorkActPage,
});
const sitePhotosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site/photos',
  component: SitePhotosPage,
  validateSearch: (s: Record<string, unknown>) => {
    const id = (v: unknown) => (typeof v === 'string' && /^[\da-f-]{36}$/i.test(v) ? v : undefined);
    return { project: id(s.project), activity: id(s.activity), sheet: id(s.sheet), remark: id(s.remark) };
  },
});
const siteReviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/site/review',
  component: SiteReviewPage,
  validateSearch: (s: Record<string, unknown>) => ({
    project: typeof s.project === 'string' && /^[\da-f-]{36}$/i.test(s.project) ? s.project : undefined,
    report: typeof s.report === 'string' && /^[\da-f-]{36}$/i.test(s.report) ? s.report : undefined,
    state:
      typeof s.state === 'string' &&
      ['queue', 'submitted', 'verified', 'returned', 'accepted', 'rejected'].includes(s.state)
        ? s.state
        : undefined,
    from: typeof s.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.from) ? s.from : undefined,
    to: typeof s.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.to) ? s.to : undefined,
    revision: Number.isInteger(Number(s.revision)) && Number(s.revision) > 0 ? Number(s.revision) : undefined,
  }),
});
const moduleDeepRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '$module/$',
  component: ModulePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  setupRoute,
  landingRoute,
  requestAccessRoute,
  signupConfirmRoute,
  inviteRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  psdShareRoute,
  appRoute.addChildren([
    homeRoute,
    projectsListRoute,
    projectRoute,
    projectTabRoute,
    inboxRoute,
    calendarRoute,
    suppliersRoute,
    supplierCabinetRoute,
    supplierCabinetRfqRoute,
    supplierRoute,
    procurementRoute,
    requestRoute,
    registryRoute,
    rfqRoute,
    contractsRoute,
    contractRoute,
    financeRoute,
    roadmapRoute,
    linksMapRoute,
    whatsNewRoute,
    startRoute,
    costArticlesRoute,
    profileRoute,
    estimatesRoute,
    estimateResourcesRoute,
    estimateRoute,
    normsRoute,
    normItemRoute,
    normReleaseRoute,
    normLibraryImportRoute,
    normLibraryReviewsRoute,
    warehouseRoute,
    warehouseDocRoute,
    warehouseItemRoute,
    warehouseCardRoute,
    warehouseInventoryRoute,
    warehouseToolRoute,
    warehouseLabelsRoute,
    qhseRoute,
    qhseInspectionRoute,
    qhseTemplateRoute,
    qhsePermitRoute,
    qhseBriefingRoute,
    qhseJournalRoute,
    qhseObservationRoute,
    qhseObserveRoute,
    videoRoute,
    fleetRoute,
    adminRoute.addChildren([
      adminIndexRoute,
      adminMembersRoute,
      adminMemberCardRoute,
      adminDelegationsRoute,
      adminRolesRoute,
      adminLeRoute,
      adminOrgRoute,
      adminAuditRoute,
      adminSettingsRoute,
      adminLicenseRoute,
      adminBackupsRoute,
      adminBrandingRoute,
      adminPrivacyRoute,
      adminFileScanRoute,
      adminIntegrationsRoute,
      adminStatusRoute,
    ]),
    platformRoute.addChildren([
      platformIndexRoute,
      platformTenantRoute,
      platformPlansRoute,
      platformRequestsRoute,
      platformInvoicesRoute,
      platformMarketsRoute,
      platformSalesRoute,
      platformEventsRoute,
      platformPilotRoute,
      platformUsersRoute,
      platformUserRoute,
      platformSecurityRoute,
      platformAnnouncementsRoute,
      platformTeamRoute,
      platformVersionsRoute,
    ]),
    supportRoute,
    supportTicketRoute,
    supportSuggestionRoute,
    psdRoute,
    psdSetRoute,
    psdSheetRoute,
    psdTransmittalRoute,
    psdFileRoute,
    psdCompareRoute,
    supervisionRoute,
    remarkRoute,
    visitRoute,
    inspectionRoute,
    questionRoute,
    changesRoute,
    changeRoute,
    schedulesRoute,
    scheduleRoute,
    contractorScheduleRoute,
    resourcesRoute,
    documentsRoute,
    lettersRoute,
    execDocsRoute,
    workJournalRoute,
    analyticsRoute,
    reportsRoute,
    reportsListsRoute,
    reportsListRoute,
    reportsBuilderRoute,
    reportsCustomRoute,
    reportsRunRoute,
    kbRoute,
    kbNewRoute,
    kbNormRoute,
    kbArticleRoute,
    siteRoute,
    siteDayRoute,
    siteMissingRoute,
    siteSummaryRoute,
    siteExtraWorksRoute,
    siteExtraWorkNewRoute,
    siteExtraWorkActRoute,
    siteReviewRoute,
    sitePhotosRoute,
    budgetIndexRoute,
    pilotRoute,
    moduleRoute,
    moduleDeepRoute,
    devboardRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPendingMs: 300,
    scrollRestoration: true,
  });
}
